import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtPct, tidyText } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { KpiTile, SparkLine, smoothSegments } from "../components/intelligence/KpiTile";
import {
  IconAlertTriangle, IconInfo,
  IconLineChart, IconCalculator,
  IconArrowUp, IconArrowDown, IconMinus, IconX,
} from "../components/icons";
import { CostsPage } from "./CostsPage";
import type { TransportDreDto, TransportDreLine } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { DreByAccountPanel } from "../components/dre/DreByAccountPanel";

type DreTab = "dre" | "custos";

// ── helpers ──────────────────────────────────────────────────────────────────

const MONTHS_PT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function fmtMonthShort(ym: string): string {
  const [, m] = ym.split("-");
  return MONTHS_PT[(parseInt(m ?? "1", 10) - 1) % 12] ?? ym;
}

function fmtMonthYear(ym: string): string {
  const [y, m] = ym.split("-");
  return `${MONTHS_PT[(parseInt(m ?? "1", 10) - 1) % 12]}/${(y ?? "").slice(2)}`;
}

function fmtPeriodDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    timeZone: "America/Sao_Paulo",
  });
}

function axisFormatter(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
}

/**
 * Tarja da DRE: margem de contribuição mês a mês, no mesmo espírito da "rota
 * do período" da Visão Executiva (curva suave, revelada com animação, ponto do
 * mês atual em destaque, linha de referência) — mas com a lógica própria do
 * DRE: tendência MENSAL do indicador mais confiável, não acumulado diário de
 * um único período. A referência aqui é a média da própria série (não há
 * "mesmo ponto do período anterior" — a DRE não guarda série diária do ano
 * passado), e o fundo tem um gradiente sutil pra dar profundidade ao traço.
 */
function DreMarginTrend({ monthly }: { monthly: TransportDreDto["monthly"] }) {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const pts = monthly.filter((m) => m.grossRevenue > 0);
  if (pts.length < 2) return null;

  // Unidade única pro gráfico inteiro: % da receita líquida (não R$) — mostra
  // a saúde da margem ao longo do tempo, sem confundir com variação de escala
  // da própria receita.
  const W = 1000, H = 70;
  const values = pts.map((m) => m.contributionMarginPct * 100);
  // Sem ancorar em 0: a margem costuma variar numa faixa estreita (ex. 78-88%)
  // — se o eixo começasse em 0%, a variação real viraria uma linha quase reta
  // encostada no topo, com a área de preenchimento tomando o quadro inteiro.
  const min = Math.min(...values);
  const max = Math.max(...values, 1);
  const span = max - min || 1;
  const x = (i: number) => (i / (pts.length - 1)) * W;
  const y = (v: number) => H - 9 - ((v - min) / span) * (H - 18);

  const points = pts.map((m, i) => ({ x: x(i), y: y(m.contributionMarginPct * 100) }));
  const segments = smoothSegments(points);
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const strokePath = `M${first.x.toFixed(2)},${first.y.toFixed(2)}${segments}`;
  const areaPath = `M${first.x.toFixed(2)},${H} L${first.x.toFixed(2)},${first.y.toFixed(2)}${segments} L${last.x.toFixed(2)},${H} Z`;
  const lastMonth = pts[pts.length - 1]!;

  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  const avgY = y(avg);

  return (
    <div className="mt-4 pt-4 border-t border-white/10">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-semibold text-tm-200 uppercase tracking-widest">Margem de contribuição, mês a mês</p>
        <p className="text-xs text-tm-100 flex items-center gap-3">
          <span>Média: <span className="font-display font-semibold text-sm text-white tabular-nums">{fmtPct(avg)}</span></span>
          <span>Mês atual: <span className="font-display font-semibold text-sm text-white tabular-nums">{fmtPct(lastMonth.contributionMarginPct * 100)}</span></span>
        </p>
      </div>
      <div className="relative h-16">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible" aria-hidden="true">
          <defs>
            <linearGradient id="dre-trend-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="rgb(var(--tm-300))" stopOpacity={0.45} />
              <stop offset="100%" stopColor="rgb(var(--tm-300))" stopOpacity={0} />
            </linearGradient>
            <clipPath id="dre-trend-reveal">
              <rect x="0" y="0" height={H} width={drawn ? W : 0} className="transition-[width] duration-[1200ms] ease-out motion-reduce:transition-none" />
            </clipPath>
          </defs>
          {/* grade horizontal sutil, pra dar profundidade */}
          <line x1="0" y1={H - 9} x2={W} y2={H - 9} stroke="rgba(255,255,255,0.12)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <line x1="0" y1={9} x2={W} y2={9} stroke="rgba(255,255,255,0.08)" strokeWidth="1" vectorEffect="non-scaling-stroke" strokeDasharray="4 4" />
          {/* linha de referência: média do período */}
          <line
            x1="0" y1={avgY} x2={W} y2={avgY}
            stroke="rgb(var(--tm-200))" strokeWidth="1.5" strokeDasharray="5 5" vectorEffect="non-scaling-stroke"
            className={`transition-opacity duration-500 delay-[900ms] motion-reduce:transition-none ${drawn ? "opacity-70" : "opacity-0"}`}
          />
          <g clipPath="url(#dre-trend-reveal)">
            <path d={areaPath} fill="url(#dre-trend-fill)" />
            <path d={strokePath} fill="none" stroke="rgb(var(--tm-300))" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          </g>
        </svg>
        <span
          className={`absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-tm-200 ring-4 ring-tm-800 shadow-lg transition-opacity duration-500 delay-[1100ms] motion-reduce:transition-none ${drawn ? "opacity-100" : "opacity-0"}`}
          style={{ left: "100%", top: `${(last.y / H) * 100}%` }}
          aria-hidden="true"
        />
      </div>
      <div className="flex justify-between text-[11px] text-tm-100/70 mt-2">
        <span>{fmtMonthShort(pts[0]!.month)}</span>
        <span>{fmtMonthShort(lastMonth.month)} (mês atual)</span>
      </div>
    </div>
  );
}

// ── DRE table row ─────────────────────────────────────────────────────────────

/**
 * Drill por linha da DRE: linhas com FONTE navegam para a memória de cálculo
 * (mesmo período da tela); linhas DERIVADAS expandem a fórmula inline.
 * Regras em docs/auditoria/MAPA_REGRAS_FINANCEIRAS.md §5.
 */
const LINE_DRILL: Record<string, { cardKey: string } | { formula: string }> = {
  gross_revenue:  { cardKey: "cte-receita-mes" },
  cte_revenue:    { cardKey: "cte-receita-mes" },
  deductions:     { cardKey: "cte-icms-mes" },
  icms:           { cardKey: "cte-icms-mes" },
  net_revenue:    { formula: "Receita Líquida = Receita Bruta − ICMS s/ serviços" },
  direct_costs:   { formula: "Custos Diretos = Frete motorista + Pedágios + Sec/Cat e Despacho" },
  driver_freight: { cardKey: "dre-frete-motorista" },
  tolls:          { cardKey: "cte-pedagio-mes" },
  other_direct:   { formula: "Sec/Cat e Despacho = colunas do próprio CT-e (compõem o mesmo documento. Abra Faturamento CT-e para ver os CT-es)" },
  contribution:   { formula: "Margem de Contribuição = Receita Líquida − Custos Diretos" },
  op_expenses:    { cardKey: "pago-no-periodo" },
  net_result:     { formula: "Resultado Estimado = Margem de Contribuição − Despesas Operacionais (⚠️ despesas incluem itens não operacionais, valor estimado)" },
};

function DreTableRow({
  line,
  prevValue,
  onClick,
  drillHint,
}: {
  line: TransportDreLine;
  prevValue: number | undefined;
  onClick?: () => void;
  drillHint?: "docs" | "formula";
}) {
  const isResult = line.role === "result";
  const isTotal  = line.role === "total";
  const isItem   = line.role === "item";

  const variation =
    prevValue != null && prevValue !== 0
      ? ((line.value - prevValue) / Math.abs(prevValue)) * 100
      : null;

  const variationColor =
    variation == null || Math.abs(variation) < 0.5
      ? "text-gray-400"
      : (line.isPositive && variation > 0) || (!line.isPositive && variation < 0)
      ? "text-tm-600"
      : "text-danger-600";

  return (
    <tr
      onClick={onClick}
      title={onClick ? (drillHint === "formula" ? "Clique para ver a fórmula" : "Clique para ver os documentos que compõem esta linha") : undefined}
      className={
        (onClick ? "cursor-pointer " : "") +
        (isResult
          ? line.value >= 0
            ? "bg-tm-50 border-l-4 border-l-tm-400"
            : "bg-danger-50 border-l-4 border-l-danger-400"
          : isTotal
          ? "bg-gray-50/70 hover:bg-gray-100/70"
          : "hover:bg-gray-50/50")
      }
    >
      {/* Label */}
      <td className="px-5 py-3">
        <div className={`flex items-center gap-2 ${isItem ? "pl-5" : ""}`}>
          <span
            className={
              isResult ? "font-bold text-gray-900 text-sm"
              : isTotal ? "font-semibold text-gray-800 text-sm"
              : "text-gray-600 text-sm"
            }
          >
            {line.label}
          </span>
          {onClick && drillHint === "docs" && (
            <span className="text-[10px] text-tm-600 opacity-70 flex-shrink-0">▸ docs</span>
          )}
          {onClick && drillHint === "formula" && (
            <span className="text-[10px] text-gray-400 opacity-70 flex-shrink-0">ƒx</span>
          )}
          {line.unreliable && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs bg-warning-100 text-warning-700 font-medium flex-shrink-0">
              <IconAlertTriangle className="w-3 h-3" />
              Estimado
            </span>
          )}
        </div>
      </td>

      {/* Value */}
      <td className="px-5 py-3 text-right whitespace-nowrap">
        <span
          className={
            isResult
              ? line.value >= 0
                ? "font-bold text-tm-700 text-sm"
                : "font-bold text-danger-700 text-sm"
              : isTotal
              ? "font-semibold text-gray-900 text-sm"
              : "text-gray-700 text-sm"
          }
        >
          {line.formatted}
        </span>
      </td>

      {/* Variation */}
      <td className="px-5 py-3 text-right whitespace-nowrap hidden md:table-cell">
        {variation != null && Math.abs(variation) >= 0.5 ? (
          <span className={`text-xs font-medium ${variationColor}`}>
            {variation > 0 ? "+" : ""}{variation.toFixed(1)}%
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* Source */}
      <td className="px-5 py-3 text-xs text-gray-400 hidden lg:table-cell max-w-[220px] truncate">
        {line.source ?? "—"}
      </td>
    </tr>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function DreManagerialTab() {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);
  // Linha derivada com fórmula expandida (clique em linhas ƒx)
  const [expandedFormula, setExpandedFormula] = useState<string | null>(null);

  // Aviso de confiabilidade: cada linha fechável individualmente, mas só pra
  // esta sessão do navegador (ou até o texto daquela linha mudar) — não é
  // "nunca mais mostrar", é "já vi, some por agora". Guardado por conteúdo,
  // não por flag fixa, pra reaparecer se o aviso virar outra coisa.
  const [dismissedWarnings, setDismissedWarnings] = useState<string[]>([]);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("dre-warnings-dismissed");
      setDismissedWarnings(raw ? (JSON.parse(raw) as string[]) : []);
    } catch { /* sessionStorage indisponível (ou lixo salvo) — aviso sempre aparece, sem crash */ }
  }, []);

  const { data: dre, isLoading, isError: dreError, refetch: dreRefetch } = useQuery({
    queryKey: ["transport", "dre", from, to, filters.comparison],
    queryFn: () =>
      api
        .get<TransportDreDto>(
          `/dashboards/transport/dre?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
        )
        ,
    staleTime: 60_000,
  });

  // ── Deltas ────────────────────────────────────────────────────────────────
  const grossDelta  = computeDelta(dre?.grossRevenue,       dre?.prevGrossRevenue,       { direction: "higher_is_better" });
  const netDelta    = computeDelta(dre?.netRevenue,         dre?.prevNetRevenue,         { direction: "higher_is_better" });
  const marginDelta = computeDelta(dre?.contributionMargin, dre?.prevContributionMargin, { direction: "higher_is_better" });
  // minBase bem menor que o padrão (100): margem em % é sempre < 100 por
  // definição, então o guard de "base pequena demais" disparava sempre e o
  // selo "Margem em alta/estável/queda" abaixo ficava travado em "estável",
  // nunca refletindo uma queda ou alta real.
  const marginPctDelta = computeDelta(
    dre ? dre.contributionMarginPct * 100 : null,
    dre ? dre.prevContributionMarginPct * 100 : null,
    { direction: "higher_is_better", minBase: 1 },
  );

  // ── Prev-value map para colunas de comparação na tabela DRE ───────────────
  // Derivamos o que não está no DTO via subtração dos escalares disponíveis
  const prevDirectCosts = dre
    ? dre.prevNetRevenue - dre.prevContributionMargin
    : 0;
  const prevIcms = dre
    ? dre.prevGrossRevenue - dre.prevNetRevenue
    : 0;

  const prevMap: Partial<Record<string, number>> = dre
    ? {
        gross_revenue:  dre.prevGrossRevenue,
        cte_revenue:    dre.prevGrossRevenue,
        deductions:     -prevIcms,
        icms:           -prevIcms,
        net_revenue:    dre.prevNetRevenue,
        direct_costs:   -prevDirectCosts,
        contribution:   dre.prevContributionMargin,
        net_result:     dre.prevNetResult,
      }
    : {};

  // ── Dados para gráficos ───────────────────────────────────────────────────
  const monthlyChartData = (dre?.monthly ?? []).map((m) => ({
    month: fmtMonthShort(m.month),
    "Receita Bruta":         m.grossRevenue,
    "Custos Diretos":        m.directCosts,
    "Margem Contribuição":   m.contributionMargin,
  }));

  // Sparklines dos KPIs: só os 3 que têm série mensal no DTO (`dre.monthly`).
  // Frete motorista/pedágios e os 3 indicadores estimados não têm quebra
  // mensal própria — sem sparkline neles, pra não inventar visual sem dado.
  const monthlyGross  = (dre?.monthly ?? []).map((m) => m.grossRevenue);
  const monthlyNet    = (dre?.monthly ?? []).map((m) => m.netRevenue);
  const monthlyMargin = (dre?.monthly ?? []).map((m) => m.contributionMargin);

  // ── Evolução anual: ano do período selecionado (vem no "YYYY-MM" de cada mês) ──
  const yearlyYear = dre?.yearly2026[0]?.month.slice(0, 4) ?? String(new Date().getFullYear());
  const total2026 = dre?.yearly2026.reduce(
    (acc, r) => ({
      cteCount:         acc.cteCount + r.cteCount,
      grossRevenue:     acc.grossRevenue + r.grossRevenue,
      icmsDeduction:    acc.icmsDeduction + r.icmsDeduction,
      netRevenue:       acc.netRevenue + r.netRevenue,
      directCosts:      acc.directCosts + r.directCosts,
      contribution:     acc.contribution + r.contributionMargin,
    }),
    { cteCount: 0, grossRevenue: 0, icmsDeduction: 0, netRevenue: 0, directCosts: 0, contribution: 0 },
  );

  // ── Labels de período ─────────────────────────────────────────────────────
  const periodLabel = `${fmtPeriodDate(from)} → ${fmtPeriodDate(new Date(new Date(to).getTime() - 1).toISOString())}`;

  return (
    <div className="space-y-6">
      {dreError && <QueryErrorBanner what="a DRE" onRetry={() => { dreRefetch(); }} />}

      {/* ── Alertas de confiabilidade ──────────────────────────────────────── */}
      {(() => {
        const visibleWarnings = (dre?.warnings ?? []).filter((w) => !dismissedWarnings.includes(w));
        if (visibleWarnings.length === 0) return null;
        return (
          // Título como legenda solta, FORA da caixa colorida: antes ele ficava
          // dentro da mesma caixa que a linha do aviso, empurrando a linha pra
          // baixo — o X centralizava certo na própria linha, mas não na caixa
          // inteira (que é o que parece "centralizado" de verdade). Cada aviso
          // agora é a própria caixa (mesmo modelo do Fluxo de Caixa), então o X
          // fica centralizado na caixa dele mesmo com 1 ou várias linhas.
          <div className="space-y-1.5">
            <p className="flex items-center gap-2 px-1 text-sm font-semibold text-warning-800">
              <IconAlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0" />
              Avisos de confiabilidade
            </p>
            {visibleWarnings.map((w) => (
              <div key={w} className="flex items-start gap-2 rounded-xl border border-warning-200 bg-warning-50 px-5 py-3">
                <p className="flex-1 text-sm text-warning-700 leading-relaxed">{w}</p>
                <button
                  type="button"
                  onClick={() => {
                    const next = [...dismissedWarnings, w];
                    try { sessionStorage.setItem("dre-warnings-dismissed", JSON.stringify(next)); } catch { /* ignora */ }
                    setDismissedWarnings(next);
                  }}
                  className="self-center flex-shrink-0 p-1 rounded-md text-warning-500 hover:bg-warning-100 hover:text-warning-700 transition-colors"
                  title="Fechar por agora — volta a aparecer numa próxima visita"
                  aria-label="Fechar aviso"
                >
                  <IconX className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Banner executivo — paleta Transmasi ──────────────────────────── */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-tm-800 to-tm-700 text-white p-6 shadow-lg">
        {/* glow decorativo, só textura — não interfere com o conteúdo */}
        <div className="pointer-events-none absolute -top-28 -right-20 w-80 h-80 rounded-full bg-tm-400/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-tm-300/10 blur-3xl" aria-hidden="true" />

        <div className="relative">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-[11px] font-semibold text-tm-200 uppercase tracking-widest">Período analisado</p>
              <p className="text-base font-semibold text-white mt-0.5">{periodLabel}</p>
            </div>
            <div className="flex items-center gap-3">
              {dre && (
                <span className="px-3 py-1.5 rounded-full bg-white/15 text-tm-100 text-xs font-medium">
                  {dre.cteCount.toLocaleString("pt-BR")} CT-es autorizados
                </span>
              )}
              {dre?.dataCompleteness.cte && (
                <span className="px-3 py-1.5 rounded-full bg-white/15 text-tm-100 text-xs font-medium">
                  Dados CT-e: OK
                </span>
              )}
            </div>
          </div>

          {/* Manchete: a margem de contribuição como protagonista da DRE */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div className="max-w-2xl">
              <h2 className="text-base text-tm-100 leading-snug">
                {isLoading || !dre ? "Carregando o período…" : (
                  <>
                    A margem de contribuição foi de{" "}
                    <span className="font-display font-semibold text-3xl text-white tabular-nums align-baseline">{fmtBRL(dre.contributionMargin)}</span>
                    {" "}— {fmtPct(dre.contributionMarginPct * 100)} da receita líquida
                  </>
                )}
              </h2>
              {!isLoading && dre && dre.prevContributionMargin !== 0 && (
                <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-tm-100 mt-1.5">
                  <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-semibold tabular-nums ${
                    marginDelta.isGood == null ? "bg-white/15 text-white" : marginDelta.isGood ? "bg-tm-300 text-tm-900" : "bg-red-500 text-white"
                  }`}>
                    {marginDelta.trend === "up" ? <IconArrowUp className="w-3.5 h-3.5" /> : marginDelta.trend === "down" ? <IconArrowDown className="w-3.5 h-3.5" /> : <IconMinus className="w-3.5 h-3.5" />}
                    {marginDelta.label ?? (marginDelta.pct != null ? fmtPct(Math.abs(marginDelta.pct)) : "—")}
                  </span>
                  vs. {fmtBRL(dre.prevContributionMargin)} ({tidyText(dre.comparisonLabel)})
                </p>
              )}
            </div>

            {!isLoading && dre && (
              <span className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium flex-shrink-0">
                <span className={`w-2.5 h-2.5 rounded-full ${
                  marginPctDelta.trend === "flat" ? "bg-tm-300" : marginPctDelta.isGood ? "bg-tm-300" : "bg-red-400"
                }`} aria-hidden="true" />
                {marginPctDelta.trend === "flat" ? "Margem estável" : marginPctDelta.isGood ? "Margem em alta" : "Margem em queda"}
                <span className="text-tm-100 font-normal">· ★ dado mais confiável da DRE</span>
              </span>
            )}
          </div>

          {/* Receita bruta/líquida — contexto de apoio à margem acima */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="border-l-2 border-white/20 pl-4">
              <p className="text-xs text-tm-100/70 mb-1">Receita Bruta CT-e</p>
              <p className="text-xl font-semibold text-white">
                {isLoading ? "Carregando…" : fmtBRL(dre?.grossRevenue ?? 0)}
              </p>
              {dre && dre.prevGrossRevenue > 0 && (
                <p className="text-xs text-tm-200 mt-1">
                  vs. {fmtBRL(dre.prevGrossRevenue)} ({tidyText(dre.comparisonLabel)})
                </p>
              )}
            </div>

            <div className="border-l-2 border-white/20 pl-4">
              <p className="text-xs text-tm-100/70 mb-1">Receita Líquida <span className="text-tm-200">(após ICMS)</span></p>
              <p className="text-xl font-semibold text-white">
                {isLoading ? "—" : fmtBRL(dre?.netRevenue ?? 0)}
              </p>
              {dre && dre.icmsDeduction > 0 && (
                <p className="text-xs text-tm-200 mt-1">
                  (-) ICMS: {fmtBRL(dre.icmsDeduction)}
                </p>
              )}
            </div>
          </div>

          {!isLoading && dre && <DreMarginTrend monthly={dre.monthly} />}
        </div>
      </div>

      {/* ── KPIs confiáveis ────────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Indicadores confiáveis
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiTile
            label="Receita Bruta CT-e"
            value={dre ? fmtBRL(dre.grossRevenue) : "—"}
            delta={grossDelta}
            deltaLabel={dre?.comparisonLabel ?? undefined}
            visual={<SparkLine values={monthlyGross} label="Receita bruta por mês" />}
            footnote="Receita bruta por mês. O ponto marca o mês atual"
            loading={isLoading}
          />
          <KpiTile
            label="Receita Líquida"
            value={dre ? fmtBRL(dre.netRevenue) : "—"}
            delta={netDelta}
            deltaLabel={dre?.comparisonLabel ?? undefined}
            visual={<SparkLine values={monthlyNet} label="Receita líquida por mês" />}
            footnote="Receita líquida por mês. O ponto marca o mês atual"
            loading={isLoading}
          />
          <KpiTile
            label="Margem Contribuição ★"
            value={dre ? fmtBRL(dre.contributionMargin) : "—"}
            delta={marginDelta}
            deltaLabel={dre?.comparisonLabel ?? undefined}
            status={dre && dre.contributionMargin < 0 ? "critical" : null}
            visual={<SparkLine values={monthlyMargin} label="Margem de contribuição por mês" />}
            footnote={dre ? `★ Dado confiável · ${fmtPct(dre.contributionMarginPct * 100)} da receita líquida` : "★ Dado confiável"}
            loading={isLoading}
          />
          <KpiTile
            label="Frete Motorista"
            value={dre ? fmtBRL(dre.driverFreightCost) : "—"}
            footnote="Custo direto: cartas frete"
            loading={isLoading}
          />
          <KpiTile
            label="Pedágios"
            value={dre ? fmtBRL(dre.tollCost) : "—"}
            footnote="Custo direto: CT-e"
            loading={isLoading}
          />
        </div>
      </div>

      {/* ── DRE por conta financeira (substitui os indicadores estimados por fact_payments) ── */}
      <DreByAccountPanel from={from} to={to} />

      {/* ── Tabela DRE hierárquica ─────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Demonstrativo de Resultado</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Estrutura hierárquica · fonte de cada linha indicada · ⚠️ = dado estimado
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dre && (
              <span className="text-xs text-gray-400 hidden sm:block">
                Período: {periodLabel}
              </span>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="p-10 text-center text-sm text-gray-400 animate-pulse">
            Calculando DRE…
          </div>
        )}

        {!isLoading && !dre && (
          <div className="p-10 text-center text-sm text-gray-400">
            Sem dados disponíveis para o período selecionado.
          </div>
        )}

        {!isLoading && dre && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-surface-border">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    Linha DRE
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Valor período
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                    vs. {tidyText(dre.comparisonLabel)}
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Fonte dos dados
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {dre.lines.map((line) => {
                  const drill = LINE_DRILL[line.key];
                  const onClick = !drill ? undefined : () => {
                    if ("cardKey" in drill) {
                      navigate(`/financeiro/memoria/${drill.cardKey}?periodFrom=${encodeURIComponent(from)}&periodTo=${encodeURIComponent(to)}`);
                    } else {
                      setExpandedFormula((cur) => (cur === line.key ? null : line.key));
                    }
                  };
                  return (
                    <React.Fragment key={line.key}>
                      <DreTableRow
                        line={line}
                        prevValue={prevMap[line.key]}
                        onClick={onClick}
                        drillHint={drill ? ("cardKey" in drill ? "docs" : "formula") : undefined}
                      />
                      {drill && "formula" in drill && expandedFormula === line.key && (
                        <tr className="bg-gray-50">
                          <td colSpan={4} className="px-8 py-2.5">
                            <code className="text-xs text-gray-600">{drill.formula}</code>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Legenda */}
        {dre && (
          <div className="px-5 py-3 border-t border-surface-border bg-gray-50/50 flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-tm-100 border border-tm-300" />
              Margem positiva
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-danger-100 border border-danger-300" />
              Resultado negativo
            </span>
            <span className="flex items-center gap-1.5">
              <IconAlertTriangle className="w-3 h-3 text-warning-500" />
              Dado estimado, interpretar com cautela
            </span>
            <span className="flex items-center gap-1.5 ml-auto">
              <IconInfo className="w-3 h-3 text-gray-400" />
              ★ Margem de Contribuição é o indicador mais confiável desta DRE
            </span>
          </div>
        )}
      </div>

      {/* ── Gráficos ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Tendência mensal */}
        <ChartCard
          title="Tendência Mensal"
          subtitle="Receita Bruta · Custos Diretos · Margem de Contribuição"
          height={260}
          loading={isLoading}
          isEmpty={monthlyChartData.length === 0}
          emptyMessage="Sem dados de tendência para exibir."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyChartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-receita-bruta" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="grad-custos-diretos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#F59E0B" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="grad-margem" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="#0F766E" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#0F766E" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={axisFormatter}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(v: number, name: string) => [fmtBRL(v), name]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="Receita Bruta"       fill="url(#grad-receita-bruta)"  radius={[3, 3, 0, 0]} maxBarSize={36} />
              <Bar dataKey="Custos Diretos"      fill="url(#grad-custos-diretos)" radius={[3, 3, 0, 0]} maxBarSize={36} />
              <Bar dataKey="Margem Contribuição" fill="url(#grad-margem)"         radius={[3, 3, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Ranking de clientes */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Top Clientes por Receita CT-e</p>
            <p className="text-xs text-gray-400 mt-0.5">Faturamento bruto no período selecionado</p>
          </div>

          {isLoading && (
            <div className="p-6 text-sm text-gray-400 text-center">Carregando…</div>
          )}

          {!isLoading && (dre?.topCustomers ?? []).length === 0 && (
            <div className="p-6 text-sm text-gray-400 text-center">
              Sem dados de clientes no período.
            </div>
          )}

          <div className="divide-y divide-surface-border">
            {(dre?.topCustomers ?? []).slice(0, 8).map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-tm-500 rounded-full transition-all"
                      style={{ width: `${Math.min(Math.max(c.sharePct, 2), 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{c.formatted}</p>
                  <p className="text-xs text-gray-400">
                    {c.sharePct.toFixed(1)}% · {c.cteCount} CT-es
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Visão anual mês a mês ──────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Evolução {yearlyYear}, Mês a Mês</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Apenas CT-e autorizados + Cartas Frete · Despesas operacionais não incluídas (dados incompletos)
            </p>
          </div>
          {total2026 && (
            <span className="text-xs text-gray-500 hidden sm:block flex-shrink-0">
              Acumulado: {fmtBRL(total2026.grossRevenue)}
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-surface-border">
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-20">
                  Mês
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  CT-es
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Receita Bruta
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                  (-) ICMS
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Rec. Líquida
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  (-) Custos
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Margem
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  %
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-surface-border">
              {(dre?.yearly2026 ?? Array.from({ length: 12 }, (_, i) => ({
                month: `${yearlyYear}-${String(i + 1).padStart(2, "0")}`,
                cteCount: 0, grossRevenue: 0, icmsDeduction: 0,
                netRevenue: 0, directCosts: 0, contributionMargin: 0,
                contributionMarginPct: 0,
              }))).map((row) => {
                const isEmpty = row.grossRevenue === 0;
                const positiveMargin = row.contributionMargin >= 0;

                return (
                  <tr
                    key={row.month}
                    className={
                      isEmpty
                        ? "opacity-40"
                        : "hover:bg-gray-50/60"
                    }
                  >
                    <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                      {fmtMonthYear(row.month)}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {isEmpty ? "—" : row.cteCount.toLocaleString("pt-BR")}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                      {isEmpty ? "—" : fmtBRL(row.grossRevenue)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 hidden sm:table-cell whitespace-nowrap">
                      {isEmpty ? "—" : `(${fmtBRL(row.icmsDeduction)})`}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                      {isEmpty ? "—" : fmtBRL(row.netRevenue)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 hidden md:table-cell whitespace-nowrap">
                      {isEmpty ? "—" : `(${fmtBRL(row.directCosts)})`}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${
                      isEmpty ? "text-gray-400"
                      : positiveMargin ? "text-tm-600"
                      : "text-danger-600"
                    }`}>
                      {isEmpty ? "—" : fmtBRL(row.contributionMargin)}
                    </td>
                    <td className={`px-4 py-3 text-right text-xs font-semibold ${
                      isEmpty ? "text-gray-400"
                      : positiveMargin ? "text-tm-600"
                      : "text-danger-600"
                    }`}>
                      {isEmpty ? "—" : fmtPct(row.contributionMarginPct * 100)}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* Linha de total acumulado */}
            {total2026 && total2026.grossRevenue > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                  <td className="px-4 py-3 text-gray-900 font-bold text-sm">
                    TOTAL {yearlyYear}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-900">
                    {total2026.cteCount.toLocaleString("pt-BR")}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">
                    {fmtBRL(total2026.grossRevenue)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 hidden sm:table-cell whitespace-nowrap">
                    ({fmtBRL(total2026.icmsDeduction)})
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">
                    {fmtBRL(total2026.netRevenue)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 hidden md:table-cell whitespace-nowrap">
                    ({fmtBRL(total2026.directCosts)})
                  </td>
                  <td className={`px-4 py-3 text-right whitespace-nowrap ${
                    total2026.contribution >= 0 ? "text-tm-700" : "text-danger-700"
                  }`}>
                    {fmtBRL(total2026.contribution)}
                  </td>
                  <td className={`px-4 py-3 text-right text-xs ${
                    total2026.contribution >= 0 ? "text-tm-700" : "text-danger-700"
                  }`}>
                    {total2026.netRevenue > 0
                      ? fmtPct((total2026.contribution / total2026.netRevenue) * 100)
                      : "—"}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          {isLoading && (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">
              Carregando evolução {yearlyYear}…
            </p>
          )}
        </div>
      </div>

    </div>
  );
}

// ── Page wrapper com abas (DRE Gerencial · Custos) ─────────────────────────────

const TABS: Array<{ id: DreTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: "dre",    label: "DRE Gerencial", icon: IconLineChart },
  { id: "custos", label: "Custos",        icon: IconCalculator },
];

const SUBTITLES: Record<DreTab, string> = {
  dre:    "Demonstrativo gerencial de Receita, Custos e Margem de Contribuição",
  custos: "Pagamentos realizados, frete motorista, pedágios e fornecedores",
};

export function DREPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: DreTab = tabParam === "custos" ? "custos" : "dre";

  function selectTab(tab: DreTab) {
    setSearchParams(tab === "dre" ? {} : { tab }, { replace: true });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="DRE / Resultado"
        subtitle={SUBTITLES[activeTab]}
      />

      {/* ── Barra de abas ──────────────────────────────────────────────────── */}
      <div className="border-b border-surface-border">
        <nav className="flex gap-1 -mb-px">
          {TABS.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => selectTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? "border-tm-500 text-tm-700"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Conteúdo da aba ────────────────────────────────────────────────── */}
      {activeTab === "dre" ? <DreManagerialTab /> : <CostsPage embedded />}
    </div>
  );
}
